import { IDocumentSimple } from '@/interfaces/deltaGpt/IDocument.ts';
import { useDeltaUserSettingsStore } from '@/stores/deltaGpt/deltaGptUserSettingsStore.ts';
import { COMMUNITY_PAGE_LINK_DEFINITIONS } from '@/utils/communityPageLinkRegistry.ts';
import { getPublicationDateTimestamp } from '@/utils/sparkContentUtils.ts';
import { getSectionRouteForSiteAreaRelevance } from '@/utils/siteAreaRelevanceUtils.ts';

interface ICommunityFeed {
    getDocumentFeedTimestamp: (document: IDocumentSimple) => number;
    getFeedAttentionDismissedTimestamp: () => number;
    isNewAttentionDocument: (document: IDocumentSimple) => boolean;
    isNewFeedDocument: (document: IDocumentSimple) => boolean;
    sortCommunityDocuments: (a: IDocumentSimple, b: IDocumentSimple) => number;
}

export function useCommunityFeed (): ICommunityFeed {
    const deltaUserSettingsStore = useDeltaUserSettingsStore();

    function getDocumentFeedTimestamp (document: IDocumentSimple): number {
        return new Date(document.created ?? '').getTime();
    }

    function parseFeedVisitTimestamp (dateString?: string | null): number {
        if (!dateString) return 0;
        const timestamp = new Date(dateString).getTime();
        return Number.isNaN(timestamp) ? 0 : timestamp;
    }

    function getFeedAttentionDismissedTimestamp (): number {
        return parseFeedVisitTimestamp(deltaUserSettingsStore.userTrackingData.feedVisitState.attentionDismissedAt);
    }

    function isNewFeedDocument (document: IDocumentSimple): boolean {
        return getDocumentFeedTimestamp(document) > getLatestFeedSeenTimestamp(document, true);
    }

    function isNewAttentionDocument (document: IDocumentSimple): boolean {
        return getDocumentFeedTimestamp(document) > getLatestFeedSeenTimestamp(document, false)
            && getDocumentFeedTimestamp(document) > getFeedAttentionDismissedTimestamp();
    }

    function getCommunityDocumentSectionRouteNames (document: IDocumentSimple): Array<string> {
        const sectionRouteNames = new Set<string>();

        document.siteAreaRelevance.forEach(siteArea => {
            const sectionRouteName = getSectionRouteForSiteAreaRelevance(siteArea);
            if (sectionRouteName) sectionRouteNames.add(sectionRouteName);
        });

        document.routeNames?.forEach(routeName => {
            const routeSectionName = getSectionRouteForSiteAreaRelevance(routeName);
            if (routeSectionName) {
                sectionRouteNames.add(routeSectionName);
                return;
            }

            const pageLinkDefinition = COMMUNITY_PAGE_LINK_DEFINITIONS.find(definition => definition.routeName === routeName);
            if (pageLinkDefinition?.sectionName) sectionRouteNames.add(pageLinkDefinition.sectionName);
        });

        return [...sectionRouteNames];
    }

    function getLatestFeedSeenTimestamp (document: IDocumentSimple, includeViewedDocument: boolean): number {
        const visitState = deltaUserSettingsStore.userTrackingData.feedVisitState;
        const sectionVisitTimestamps = getCommunityDocumentSectionRouteNames(document)
            .map(sectionRouteName => parseFeedVisitTimestamp(visitState.sectionFeedLastVisitedAtByRouteName[sectionRouteName]));

        return Math.max(
            parseFeedVisitTimestamp(visitState.yourFeedLastVisitedAt),
            parseFeedVisitTimestamp(visitState.allFeedLastVisitedAt),
            ...sectionVisitTimestamps,
            includeViewedDocument && deltaUserSettingsStore.isDocumentViewed(document) ? getDocumentFeedTimestamp(document) : 0,
        );
    }

    function sortCommunityDocuments (a: IDocumentSimple, b: IDocumentSimple): number {
        return getPublicationDateTimestamp(b.publicationDate) - getPublicationDateTimestamp(a.publicationDate);
    }

    return {
        getDocumentFeedTimestamp,
        getFeedAttentionDismissedTimestamp,
        isNewAttentionDocument,
        isNewFeedDocument,
        sortCommunityDocuments,
    };
}
